import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { useGetInboxesWithCustom } from '@/api/chat'

import {
  dispatchSetLoadingMessages,
  isLoadingMessages,
  selectClosedConversationsData,
  selectAssignedConversationsData,
} from '../../../../reducers/chatReducer'
import useChatMessages from '../../useChatMessages'
import Icon from '../../../Icons'
import Avatar from '../../../../Avatar'
import DropdownDots from '../../../DropdownDots/DropdownDots'
import InfiniteScroll from '../../../InfiniteScroll/InfiniteScroll'
import Loader from '../../../Loader/Loader'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import ImagesUpload from '../../../ImagesUpload/ImagesUpload'
import CustomReplyWindow from '../CustomReplyWindow/CustomReplyWindow'
import {
  isMaintenanceRequestChatType,
  getChatTypeFromConversation,
  smallChatOptions,
  renderProperStatusPill,
  getMaintenanceRequestId,
} from '../../helpers'
import { getFormatedDate } from '../../../../helpers/helpers'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import {
  getSMScounter,
  updateChatClosedAtFirebase,
} from '../../../../firebase/privateChat/firebaseActions'
import API from '../../../../api/api'

import Message from './../../Body/Message'
import ToggleWithText from './../../../ToggleWithText/ToggleWithText'

const ConversationWithTab = ({
  selectedChat,
  handleBack,
  handleClose,
  currentUser,
  closedConversationsData,
  assignedConversationData,
  selectedBuilding,
  showAssigneeWindowForConversation,
  availableManagementUsers,
  highManagementUsers,
  dispatchSetLoadingMessages,
  isLoadingMessages,
}) => {
  const { t } = useTranslation(['chat'])
  const navigate = useNavigate()

  const { data: chatInboxes } = useGetInboxesWithCustom({
    buildingId: selectedBuilding?.id,
    chatCustom: selectedBuilding?.chatCustom,
  })
  const [customRepliesWindowVisible, setCustomRepliesWindowVisible] = useState(false)
  const [providedText, setProvidedText] = useState()
  const [sendMessageAsSMS, setSendMessageAsSMS] = useState(false)
  const [textingOptOutInfo, setTextingOptOutInfo] = useState(false)
  const [maintenanceRequestDetails, setMaintenanceRequestDetails] = useState(null)

  const scrollDiv = useRef()
  const textInputRef = useRef(null)

  const {
    isLoadedAll,
    orderedMessages,
    smsMessagesLeft,
    isSendByUs,
    fetchMoreMessages,
    onSend,
    onReSendSMS,
    onSendWithAttachements,
    setSmsMessagesLeft,
  } = useChatMessages({
    selectedChat,
    availableManagementUsers,
    currentUser,
    sendMessageAsSMS,
    selectedBuildingId: selectedBuilding?.id,
    cancelLoadingMessages: () => dispatchSetLoadingMessages(false),
    highManagementUsers,
  })
  const chatType = getChatTypeFromConversation(selectedChat)
  const isChatMTRQ = isMaintenanceRequestChatType(selectedChat)
  const selectedChatInbox = chatInboxes
    ? chatInboxes.find((inbox) => inbox.type === chatType)
    : null
  const activeSMS =
    selectedChatInbox &&
    selectedChatInbox.enableSMS &&
    selectedChatInbox.phone !== 0 &&
    !isChatMTRQ &&
    !textingOptOutInfo
  // !isLoading
  const isChatOrAvailableSMS = (sendMessageAsSMS && smsMessagesLeft) || !sendMessageAsSMS
  const customReplies = selectedChatInbox ? selectedChatInbox.customReplies : []

  const isClosed = (conversation) => {
    return closedConversationsData.find((conv) => conv.name === conversation.channelName)
  }

  const isAssigned = (conversation) => {
    const assignedIdObject = assignedConversationData.find(
      (assigned) => assigned.name === conversation.channelName,
    )
    const assignedUserData =
      assignedIdObject && availableManagementUsers
        ? availableManagementUsers.find((userObject) => userObject.id === assignedIdObject.assignee)
        : null

    return assignedUserData ? `${assignedUserData.firstName} ${assignedUserData.lastName}` : null
  }

  const onHandleCloseOrReopenConversation = (conversation) => {
    const isClosedAt = closedConversationsData.find(
      (conv) => conv.name === conversation.channelName,
    )
      ? null
      : new Date().toISOString()
    updateChatClosedAtFirebase(selectedBuilding?.id, conversation.channelName, isClosedAt)
  }

  const onSendTextMessage = () => {
    if (providedText.trim()) {
      const newProvidedText = providedText
      setProvidedText('')
      onSend(newProvidedText)
    }
  }

  const onEnterPress = (e) => {
    if (e.shiftKey && e.key === 'Enter' && providedText.trim()) {
      return null
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (providedText.trim()) {
        onSendTextMessage()
      } else {
        setProvidedText('')
      }
    }
  }

  const pickCustomReply = async (replyData) => {
    setCustomRepliesWindowVisible(false)
    setProvidedText(replyData.message)
    textInputRef.current.focus()
  }

  const renderLastItem = () => {
    const closedConversationData = closedConversationsData.find(
      (conv) => conv.name === selectedChat.channelName,
    )
    if (closedConversationData) {
      return (
        <div className="message-closed-conversation">
          {t('chat:messagesContainer.closedOn', {
            date: getFormatedDate(closedConversationData.date, selectedBuilding.timezone),
          })}
        </div>
      )
    }
    return null
  }

  const goToSpecificMaintenanceRequest = () => {
    const rootRoute = `/dashboard/building-dashboard/maintenance/requests/${selectedBuilding?.id}/${maintenanceRequestDetails?.id}`
    navigate(rootRoute)
  }

  useEffect(() => {
    if (selectedChat) {
      getSMScounter(selectedBuilding?.id).then((count) => setSmsMessagesLeft(count))
      if (isChatMTRQ) {
        setSendMessageAsSMS(false)
      }

      if (isChatMTRQ) {
        const reqId = getMaintenanceRequestId(selectedChat.channelName)
        try {
          API.getMaintenanceRequestDetailsForChat(reqId).then(({ data }) => {
            setMaintenanceRequestDetails(data)
          })
        } catch (e) {
          console.error(e.message)
        }
      }

      try {
        API.getChatAdditionalInfo(selectedChat.channelName).then(({ data }) => {
          setTextingOptOutInfo(data.smsOptOut)
        })
      } catch (err) {
        console.error(err.message)
        throw new Error(err)
      }
    }
  }, [selectedChat])

  return (
    <div className="conversation-with-tab">
      <div className="tab-navbar">
        <div className="left-side">
          <div className="back-button" onClick={handleBack}>
            <Icon icon="arrow" />
          </div>
          <Avatar size="big">
            <img
              src={getAssetLink({ path: 'chitown-avatar/avatar', file: selectedChat.avatar })}
              alt=""
            />
          </Avatar>
          <div className="conversation-data-container">
            <div className="conversation-data">
              <div className="conversation-name">{selectedChat.name}</div>
              <div className="conversation-apt">#{selectedChat.apartment}</div>
            </div>
          </div>
        </div>
        <div className="right-side">
          <DropdownDots
            style={{ fill: '#fff' }}
            options={smallChatOptions(
              isClosed(selectedChat),
              isAssigned(selectedChat),
              () => showAssigneeWindowForConversation(selectedChat),
              () => onHandleCloseOrReopenConversation(selectedChat),
            )}
          />
          <div
            className="resize-button"
            onClick={() => navigate(`/dashboard/chat?channelName=${selectedChat.channelName}`)}
          >
            <Icon icon="resize" viewBox={'0 0 25 20'} />
          </div>
          <div className="close-button" onClick={handleClose}>
            <Icon icon="cross" />
          </div>
        </div>
      </div>
      <div className="tab-body">
        {isChatMTRQ && (
          <div className="maintenance-request-container">
            <div className="maintenance-request-details">
              <div className="request-id">
                {t('chat:conversationWithTab.requestId', {
                  id: maintenanceRequestDetails?.id ?? '',
                })}
              </div>
              <div className="request-status">
                {maintenanceRequestDetails &&
                  renderProperStatusPill(maintenanceRequestDetails.status)}
              </div>
            </div>
            <div className="view-req-details" onClick={goToSpecificMaintenanceRequest}>
              <span>{t('chat:conversationWithTab.view')}</span>
              <Icon icon="arrow" viewBox="0 2 16 16" />
            </div>
          </div>
        )}
        <div className="messages">
          {isLoadingMessages ? (
            <div
              className="loader-container"
              style={
                !activeSMS && !isChatMTRQ
                  ? { height: 'calc(100vh - 308px)', maxHeight: '525px' }
                  : {}
              }
            >
              <Loader />
            </div>
          ) : (
            <InfiniteScroll
              reference={scrollDiv}
              data={orderedMessages.map((mess) => ({ ...mess, id: mess._id }))}
              style={
                !activeSMS && !isChatMTRQ
                  ? { height: 'calc(100vh - 308px)', maxHeight: '525px' }
                  : {}
              }
              renderItem={(message) => (
                <Message
                  key={message._id}
                  message={message}
                  sendByUs={isSendByUs(message)}
                  reSendSMS={onReSendSMS}
                />
              )}
              renderLastItem={renderLastItem}
              onEndReached={() => fetchMoreMessages()}
              isLoadedAll={isLoadedAll}
              inverse={true}
            />
          )}
        </div>
        <div className="inputs-action-container">
          <TextField
            id="input-id"
            name="textInput"
            forwardedRef={textInputRef}
            value={
              isChatOrAvailableSMS ? providedText : t('chat:conversationWithTab.outOfSmsCredits')
            }
            onChange={(e) => setProvidedText(e.target.value)}
            placeholder={t('chat:messagesContainer.typeMessage')}
            onKeyPress={(e) => onEnterPress(e)}
            disabled={!isChatOrAvailableSMS}
            isError={!isChatOrAvailableSMS}
          />
          <div className="icon-container" onClick={() => setCustomRepliesWindowVisible(true)}>
            <Icon icon="message" viewBox="5 3 23 24" />
          </div>
          <div className={`icon-container ${sendMessageAsSMS ? 'is-disabled' : ''}`}>
            <ImagesUpload
              isDisabled={sendMessageAsSMS}
              pickImages={(files) => {
                onSendWithAttachements(files)
                setProvidedText('')
              }}
              limit={10}
            />
          </div>
          <div
            className={`icon-container ${!providedText && 'is-disabled'}`}
            onClick={onSendTextMessage}
          >
            <Icon icon="send" viewBox="0 0 25 20" />
          </div>
        </div>
        {activeSMS && (
          <div className="toggles-container">
            <div>{t('chat:messagesContainer.sendingMessagesAs')}</div>
            <ToggleWithText
              text={t('chat:toggle.chat')}
              isChecked={!sendMessageAsSMS}
              onChange={() => setSendMessageAsSMS(!sendMessageAsSMS)}
            />
            <ToggleWithText
              text={t('chat:toggle.sms')}
              color="#82D7B3"
              isChecked={sendMessageAsSMS}
              onChange={() => setSendMessageAsSMS(!sendMessageAsSMS)}
            />
          </div>
        )}
      </div>
      <CustomReplyWindow
        isOpen={customRepliesWindowVisible}
        data={customReplies}
        onSelectAction={pickCustomReply}
        onClose={() => setCustomRepliesWindowVisible(false)}
      />
    </div>
  )
}

ConversationWithTab.propTypes = {
  selectedChat: PropTypes.object,
  handleBack: PropTypes.func,
  handleClose: PropTypes.func,
  currentUser: PropTypes.object,
  closedConversationsData: PropTypes.array,
  selectedBuilding: PropTypes.object,
  assignedConversationData: PropTypes.array,
  showAssigneeWindowForConversation: PropTypes.func,
  availableManagementUsers: PropTypes.array,
  highManagementUsers: PropTypes.array,
  dispatchSetLoadingMessages: PropTypes.func,
  isLoadingMessages: PropTypes.bool,
}

export default connect(
  (state) => {
    return {
      isLoadingMessages: isLoadingMessages(state),
      closedConversationsData: selectClosedConversationsData(state),
      assignedConversationData: selectAssignedConversationsData(state),
    }
  },
  {
    dispatchSetLoadingMessages,
  },
)(ConversationWithTab)
